import ButtonT from '@components/tailwind-components/Button/ButtonT';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from '../../../hooks/modal/useModal';
import type { GeneralPromptModalProps } from './modals';

export const GeneralPromptModal: React.FC<GeneralPromptModalProps> = ({
  description,
  onSubmit,
  onAbort,
  type,
  shouldAutomaticallyClose = true,
  customButtons,
}) => {
  const { hideModal } = useModal();
  const { t } = useTranslation();

  const { text, variant } = useMemo((): {
    text: string;
    variant: 'primary-dark' | 'danger' | 'primary';
  } => {
    switch (type) {
      case 'confirm': {
        return {
          text: t('general.buttons.confirm'),
          variant: 'primary',
        };
      }
      case 'delete': {
        return {
          text: t('general.buttons.delete'),
          variant: 'danger',
        };
      }
      default: {
        return {
          text: t('general.buttons.submit'),
          variant: 'primary',
        };
      }
    }
  }, [t, type]);

  // Render custom buttons if provided
  const renderButtons = () => {
    if (customButtons && customButtons.length > 0) {
      return (
        <>
          {/* Always show cancel button first */}
          <ButtonT
            type='button'
            styling='cancel'
            size='xs'
            rounding='md'
            onClick={() => (onAbort ? onAbort() : hideModal())}>
            {t('general.buttons.cancel')}
          </ButtonT>

          {/* Render custom buttons */}
          {customButtons.map((button, index) => (
            <ButtonT
              key={index}
              type='button'
              styling={button.styling || 'primary'}
              size='xs'
              rounding='md'
              onClick={() => {
                button.onClick();
                shouldAutomaticallyClose && hideModal();
              }}>
              {button.text}
            </ButtonT>
          ))}
        </>
      );
    }

    // Default buttons for generic modals.
    return (
      <>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={() => (onAbort ? onAbort() : hideModal())}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling={variant}
          size='xs'
          rounding='md'
          onClick={() => {
            onSubmit?.();
            shouldAutomaticallyClose && hideModal();
          }}>
          {text}
        </ButtonT>
      </>
    );
  };

  return (
    <div className='tailwind-modal-form'>
      <div className='tailwind-modal-body'>
        <div className='text-sm font-normal'>{description}</div>
      </div>
      <div className='tailwind-modal-footer'>{renderButtons()}</div>
    </div>
  );
};

export default GeneralPromptModal;
